// Copyright (C) 2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useState } from 'react';
import { useHistory } from 'react-router';
import Card from 'antd/lib/card';
import Descriptions from 'antd/lib/descriptions';
import { MoreOutlined } from '@ant-design/icons';
import Dropdown from 'antd/lib/dropdown';

import { Job, User } from 'cvat-core-wrapper';
import { useCardHeightHOC } from 'utils/hooks';
import Preview from 'components/common/preview';
import JobActionsMenu from 'components/job-item/job-actions-menu';
import { GetJobStageChinese, GetJobStateChinese } from 'cvat-core/src/enums';
import moment from 'moment';

const useCardHeight = useCardHeightHOC({
    containerClassName: 'cvat-jobs-page',
    siblingClassNames: ['cvat-jobs-page-pagination', 'cvat-jobs-page-top-bar'],
    paddings: 40,
    minHeight: 200,
    numberOfRows: 3,
});

interface Props {
    job: Job;
    user: User;
    onJobUpdate: (job: Job) => void;
}

function JobCardComponent(props: Props): JSX.Element {
    const { job, user, onJobUpdate } = props;
    const [expanded, setExpanded] = useState<boolean>(false);
    const history = useHistory();
    const height = useCardHeight();
    const onClick = (event: React.MouseEvent): void => {
        const url = `/tasks/${job.taskId}/jobs/${job.id}`;
        if (event.ctrlKey) {
            window.open(url, '_blank', 'noopener noreferrer');
        } else {
            history.push(url);
        }
    };

    return (
        <Card
            onMouseEnter={() => setExpanded(true)}
            onMouseLeave={() => setExpanded(false)}
            style={{ height }}
            className='cvat-job-page-list-item'
            cover={(
                <>
                    <Preview
                        job={job}
                        onClick={job.locked ? () => {} : onClick}
                        loadingClassName='cvat-job-item-loading-preview'
                        emptyPreviewClassName='cvat-job-item-empty-preview'
                        previewWrapperClassName='cvat-jobs-page-job-item-card-preview-wrapper'
                        previewClassName='cvat-jobs-page-job-item-card-preview'
                    />
                    <div className='cvat-job-page-list-item-id'>
                        ID:
                        {` ${job.id}`}
                        {job.locked ? ' - 已被锁定' : ''}
                    </div>
                    <div className='cvat-job-page-list-item-dimension'>{job.dimension.toUpperCase()}</div>
                </>
            )}
        >
            <Descriptions column={1} size='small'>
                <Descriptions.Item label='阶段'>{GetJobStageChinese(job.stage)}</Descriptions.Item>
                <Descriptions.Item label='状态'>{GetJobStateChinese(job.state)}</Descriptions.Item>
                <Descriptions.Item label='创建时间'>{moment(job.createdDate).locale('zh_cn').fromNow()}</Descriptions.Item>
                <Descriptions.Item label='更新时间'>{moment(job.updatedDate).locale('zh_cn').fromNow()}</Descriptions.Item>
                { expanded ? (
                    <Descriptions.Item label='帧的数量'>{job.stopFrame - job.startFrame + 1}</Descriptions.Item>
                ) : null}
                { expanded && job.assignee ? (
                    <Descriptions.Item label='标注人员'>{job.assignee.username}</Descriptions.Item>
                ) : null}
                { expanded && job.auditor ? (
                    <Descriptions.Item label='审核人员'>{job.auditor.username}</Descriptions.Item>
                ) : null}
                { expanded ? (
                    <Descriptions.Item label='已解决问题'>{job.resolvedIssuesCount}</Descriptions.Item>
                ) : null}
                { expanded ? (
                    <Descriptions.Item label='未解决问题'>{job.issuesCount - job.resolvedIssuesCount }</Descriptions.Item>
                ) : null}
            </Descriptions>
            <Dropdown overlay={<JobActionsMenu onJobUpdate={onJobUpdate} job={job} user={user} />}>
                <MoreOutlined className='cvat-job-card-more-button' />
            </Dropdown>
        </Card>
    );
}

export default React.memo(JobCardComponent);
